import React from 'react'
import PropTypes from 'prop-types'
import {useParams} from 'react-router-dom'
import loadable from '@loadable/component'
import {HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {useCategory} from '@salesforce/commerce-sdk-react'
import LoadingSpinner from '../../components/loading-spinner'
import ProductExplore from '../product-explore'
import ProductList from '../product-list'

const PageNotFound = loadable(() => import('../page-not-found'))

/**
 * CategoryRouting component that routes to either ProductExplore or ProductList based on c_isPEP category attribute.
 *
 * Note: it currently statically links to both pages because `loadable` breaks preloading of data on SSR when used outside route components.
 *
 * @param {string} categoryIdOverride - Optional categoryId prop to override the categoryId from the URL params.
 * @param {object} props - Additional props to pass to the routed component.
 * @returns {Element}
 * @constructor
 */
const CategoryRouting = ({categoryId: categoryIdOverride, ...props}) => {
    const params = useParams()

    const categoryId = categoryIdOverride || params.categoryId

    const {
        error: categoryError,
        data: category,
        isLoading
    } = useCategory(
        {
            parameters: {
                id: categoryId,
                levels: 0
            }
        },
        {
            enabled: Boolean(categoryId)
        }
    )

    const categoryErrorStatus = categoryError?.response?.status
    switch (categoryErrorStatus) {
        case undefined:
            // No Error.
            break
        case 404:
            return <PageNotFound {...props} />
        default:
            throw new HTTPError(categoryErrorStatus, `HTTP Error ${categoryErrorStatus} occurred.`)
    }

    if (isLoading) {
        return <LoadingSpinner />
    }

    if (category.c_isPEP) {
        return <ProductExplore {...props} categoryId={categoryId} />
    }

    return <ProductList {...props} categoryId={categoryId} />
}

CategoryRouting.propTypes = {
    categoryId: PropTypes.string
}

export default CategoryRouting
